export const INVENTORY_COLUMNS = ["id", "name", "displayCategory", "subcategory", "price", "inStock", "stockQuantity", "stockMax", "deal", "bundleQty", "size", "description", "selections", "unavailableSelections"];

export function validateInventory(input) {
  const source = Array.isArray(input) ? input : input?.version === 1 ? input.items : null;
  if (!Array.isArray(source) || !source.length) throw new Error("Inventory must contain a nonempty list of products.");
  const items = source.map(item => item && ({ stockQuantity: null, description: "", unavailableSelections: [], ...item, bundleQty: item.bundleQty ?? 0 }));
  const ids = new Set();
  const departments = ["Beauty & Personal Care", "Apparel", "Shoes", "Toys", "Household", "Seasonal & Miscellaneous"];
  const pathOK = p => typeof p === "string" && (/^(images|product_photos)\//.test(p) || /^storage:product-photos\/[a-zA-Z0-9.-]+$/.test(p)) && !p.split("/").some(v => v === ".." || v === ".") && !/[\\?#]/.test(p);
  items.forEach((item, index) => {
    const fail = message => { throw new Error(`Row ${index + 1} (ID ${item?.id ?? "missing"}): ${message}`); };
    if (!item || !Number.isSafeInteger(item.id) || item.id < 1 || ids.has(item.id)) fail("ID must be a unique positive whole number.");
    ids.add(item.id);
    if (typeof item.name !== "string" || !item.name.trim()) fail("Name is required.");
    if (!departments.includes(item.displayCategory)) fail("Choose an existing department.");
    if (typeof item.subcategory !== "string") fail("Subcategory must be text.");
    if (!Number.isFinite(item.price) || item.price < 0) fail("Price must be a nonnegative number.");
    if (typeof item.inStock !== "boolean") fail("In stock must be TRUE or FALSE.");
    if (item.stockQuantity != null && (!Number.isSafeInteger(item.stockQuantity) || item.stockQuantity < 0)) fail("Stock quantity must be blank or a nonnegative whole number.");
    if (item.stockQuantity === 0) item.inStock = false;
    if (!Number.isInteger(item.stockMax) || item.stockMax < 0 || item.stockMax > 10) fail("Quantity limit must be a whole number from 0 to 10.");
    if (item.inStock && item.stockMax === 0) fail("Set inStock to FALSE when the quantity limit is zero.");
    if (typeof item.deal !== "string" || !Number.isInteger(item.bundleQty) || item.bundleQty < 0) fail("Bundle fields are invalid.");
    if (item.deal && (item.bundleQty < 2 || !((/^\d+ for \$\d+(\.\d{1,2})?$/.test(item.deal) && Number(item.deal.split(" ")[0]) === item.bundleQty) || /^\$\d+(\.\d{1,2})? for a (set|pair)$/.test(item.deal)))) fail("Use 4 for $25 with matching bundleQty, or $8 for a set/pair with its piece count.");
    if (!item.deal && item.bundleQty !== 0) fail("Clear bundleQty to 0 when removing a deal.");
    for (const key of ["selections", "unavailableSelections", "subcategories"]) {
      if (item[key] !== undefined && (!Array.isArray(item[key]) || item[key].some(v => typeof v !== "string" || !v.trim()) || new Set(item[key]).size !== item[key].length)) fail(`${key} must contain unique, nonempty text values.`);
    }
    if ((item.unavailableSelections || []).some(v => !(item.selections || []).includes(v))) fail("Unavailable options must also appear in selections.");
    if (item.selectionPrices && (typeof item.selectionPrices !== "object" || Array.isArray(item.selectionPrices) || Object.entries(item.selectionPrices).some(([k,v]) => !(item.selections || []).includes(k) || !Number.isFinite(v) || v < 0))) fail("Option prices must match options and be nonnegative.");
    if (!pathOK(item.image) || !Array.isArray(item.images) || !item.images.length || !item.images.every(pathOK)) fail("Photos must use paths inside images/ or product_photos/.");
    for (const key of ["description", "size", "selectionLabel"]) if (item[key] !== undefined && typeof item[key] !== "string") fail(`${key} must be text.`);
  });
  return items.map(item => ({ ...item }));
}

// RFC 4180 fields, including commas, quotes and embedded newlines.
export function parseCSV(text) {
  const rows = []; let row = [], value = "", quoted = false, closed = false;
  const source = text.replace(/^\uFEFF/, "").replace(/\r\n/g, "\n");
  for (let i = 0; i < source.length; i++) {
    const c = source[i];
    if (quoted) {
      if (c === '"' && source[i + 1] === '"') { value += '"'; i++; }
      else if (c === '"') { quoted = false; closed = true; }
      else value += c;
    } else if (c === '"') {
      if (value || closed) throw new Error("Invalid CSV quoting.");
      quoted = true;
    } else if (c === ',' || c === '\n') {
      row.push(value); value = ""; closed = false;
      if (c === '\n') { if (row.some(v => v !== "")) rows.push(row); row = []; }
    } else {
      if (closed) throw new Error("Unexpected text after a CSV quote.");
      value += c;
    }
  }
  if (quoted) throw new Error("CSV contains an unclosed quote.");
  row.push(value); if (row.some(v => v !== "")) rows.push(row);
  return rows;
}

export function inventoryCSV(items) {
  const quote = value => `"${String(value ?? "").replace(/"/g, '""')}"`;
  return "\uFEFF" + [INVENTORY_COLUMNS, ...items.map(item => INVENTORY_COLUMNS.map(key => Array.isArray(item[key]) ? item[key].join(" | ") : typeof item[key] === "boolean" ? String(item[key]).toUpperCase() : item[key] ?? ""))].map(row => row.map(quote).join(",")).join("\r\n");
}

export function importInventory(text, filename, baseline) {
  if (/\.json$/i.test(filename)) return validateInventory(JSON.parse(text));
  if (!/\.csv$/i.test(filename)) throw new Error("Upload a CSV or inventory.json file. Save Excel files as CSV UTF-8 first.");
  const [headers, ...rows] = parseCSV(text);
  if (!headers || headers.length !== INVENTORY_COLUMNS.length || !INVENTORY_COLUMNS.every(k => headers.includes(k))) throw new Error("Keep the original inventory CSV column headings.");
  const seen = new Set();
  const items = rows.map((values, index) => {
    if (values.length !== headers.length) throw new Error(`CSV row ${index + 2} has the wrong number of columns.`);
    const data = Object.fromEntries(headers.map((k,i) => [k, values[i]]));
    const id = Number(data.id);
    const original = baseline.find(item => item.id === id);
    if (!original) throw new Error(`Unknown ID ${data.id}. CSV updates existing products; use JSON for new products and photo paths.`);
    if (seen.has(id)) throw new Error(`Duplicate ID ${id}.`);
    seen.add(id);
    for (const k of ["id", "price", "stockMax", "bundleQty"]) {
      if (!data[k].trim()) throw new Error(`ID ${id}: ${k} cannot be blank.`);
      data[k] = Number(data[k]);
    }
    if (!/^(true|false)$/i.test(data.inStock.trim())) throw new Error(`ID ${id}: inStock must be TRUE or FALSE.`);
    data.inStock = data.inStock.trim().toLowerCase() === "true";
    data.stockQuantity = data.stockQuantity.trim() === "" ? null : Number(data.stockQuantity);
    for (const k of ["selections", "unavailableSelections"]) data[k] = data[k].split("|").map(v => v.trim()).filter(Boolean);
    return { ...original, ...data };
  });
  if (baseline.some(item => !seen.has(item.id))) throw new Error("Some products are missing. Keep all rows; mark sold items FALSE instead of deleting them.");
  return validateInventory(items);
}

export function inventoryJSON(items) {
  return JSON.stringify({ version: 1, updatedAt: new Date().toISOString(), items: validateInventory(items) }, null, 2);
}

export function inventoryChanges(before, after) {
  const old = new Map(before.map(item => [item.id, item]));
  return after.filter(item => JSON.stringify(item) !== JSON.stringify(old.get(item.id))).map(item => ({ id: item.id, name: item.name, fields: Object.keys(item).filter(k => JSON.stringify(item[k]) !== JSON.stringify(old.get(item.id)?.[k])) }));
}
